import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/utils/apiConfig';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaEye, FaSearch, FaBox, FaImage } from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import axios from 'axios';
import { fetchAdminProducts, clearError } from '../../../redux/Slices/AdminProductsSlice';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AdminProducts = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { products, pagination, loading, error } = useSelector((state) => state.adminProducts);
    
    const [currentPage, setCurrentPage] = useState(1);
    const [searchName, setSearchName] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubCategory, setSelectedSubCategory] = useState('');
    const [selectedStore, setSelectedStore] = useState('');
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [stores, setStores] = useState([]);
    
    // Pagination states for dropdowns
    const [categoriesPage, setCategoriesPage] = useState(1);
    const [subCategoriesPage, setSubCategoriesPage] = useState(1);
    const [storesPage, setStoresPage] = useState(1);
    const [categoriesPagination, setCategoriesPagination] = useState({});
    const [subCategoriesPagination, setSubCategoriesPagination] = useState({});
    const [storesPagination, setStoresPagination] = useState({});

    useEffect(() => {
        setTitle('إدارة المنتجات');
        fetchCategories();
        fetchStores();
    }, [setTitle]);

    /** يتحقق أن الرابط من صفحة Google Drive (وليس CDN عادي يحتوي ?id=) */
    function isGoogleDrivePageUrl(url) {
        if (!url || typeof url !== 'string') return false;
        try {
            const u = new URL(url.trim().startsWith('//') ? `https:${url}` : url);
            const h = u.hostname.toLowerCase();
            return h === 'drive.google.com' || h === 'docs.google.com';
        } catch {
            return false;
        }
    }

    /** يستخرج file id من روابط Drive فقط */
    function extractGoogleDriveFileId(url) {
        if (!url || typeof url !== 'string') return null;
        if (!isGoogleDrivePageUrl(url)) return null;
        const fromPath = url.match(/\/d\/([^/]+)/);
        if (fromPath?.[1]) return fromPath[1];
        const fromQuery = url.match(/[?&]id=([^&]+)/);
        return fromQuery?.[1] ?? null;
    }

    /**
     * صور عادية (S3، CDN، مسار مباشر): تُرجع كما هي.
     * روابط Google Drive (عرض الملف): تُحوَّل لرابط مناسب لـ <img>.
     */
    function resolveProductImageUrl(url) {
        if (!url || typeof url !== 'string') return '';
        const trimmed = url.trim();
        if (!trimmed) return '';
        try {
            const fileId = extractGoogleDriveFileId(trimmed);
            if (fileId) {
                return `https://drive.google.com/uc?export=view&id=${fileId}`;
            }
            return trimmed;
        } catch {
            return trimmed;
        }
    }

    useEffect(() => {
        dispatch(fetchAdminProducts({ 
            page: currentPage, 
            search: searchName,
            category_id: selectedCategory,
            subcategory_id: selectedSubCategory,
            store_id: selectedStore
        }));
    }, [dispatch, currentPage, searchName, selectedCategory, selectedSubCategory, selectedStore]);

    useEffect(() => {
        if (selectedCategory) {
            setSubCategoriesPage(1);
            fetchSubCategories(selectedCategory, 1);
        } else {
            setSubCategories([]);
            setSelectedSubCategory('');
        }
    }, [selectedCategory]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const getAuthToken = () => {
        const adminData = sessionStorage.getItem('adminkhadamat');
        if (adminData) {
            try {
                const parsed = JSON.parse(adminData);
                // البحث عن التوكن في أماكن مختلفة
                if (parsed.data?.token?.access_token) {
                    return String(parsed.data.token.access_token);
                }
                if (parsed.data?.access_token) {
                    return String(parsed.data.access_token);
                }
                if (parsed.data?.token) {
                    return String(parsed.data.token);
                }
                if (parsed.token) {
                    return String(parsed.token);
                }
                return null;
            } catch {
                return null;
            }
        }
        return null;
    };

    

    const fetchCategories = async (page = 1) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/categories?page=${page}&per_page=10`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data?.data) {
                setCategories(response.data.data);
                setCategoriesPagination(response.data.pagination || {});
            }
        } catch (error) {
        }
    };

    const fetchSubCategories = async (categoryId, page = 1) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/category/${categoryId}/sub-categories?page=${page}&per_page=10`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data?.data) {
                setSubCategories(response.data.data);
                setSubCategoriesPagination(response.data.pagination || {});
            }
        } catch (error) {
        }
    };

    const fetchStores = async (page = 1) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/stores?page=${page}&per_page=10`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data?.data?.data) {
                setStores(response.data.data.data);
                setStoresPagination(response.data.data.pagination || {});
            }
        } catch (error) {
        }
    };

    const handleViewDetails = (productId) => {
        navigate(`/admin-products/${productId}`);
    };

    const handleSearch = () => {
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setSearchName('');
        setSelectedCategory('');
        setSelectedSubCategory('');
        setSelectedStore('');
        setCurrentPage(1);
    };

    const getStatusBadge = (status) => {
        if (status === true || status === "1" || status === 1) {
            return <span className="badge bg-success text-white">نشط</span>;
        }
        return <span className="badge bg-danger text-white">غير نشط</span>;
    };

    const renderPagination = () => {
        if (!pagination) return null;


        // حساب إجمالي الصفحات بناءً على البيانات المتاحة
        const totalItems = pagination.total || 0;
        const itemsPerPage = 10; // ثابت حسب API
        const totalPages = Math.ceil(totalItems / itemsPerPage);


        return (
            <EnhancedPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                showInfo={true}
                className="mt-4"
            />
        );
    };

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إدارة المنتجات</h4>
                </div>

                {/* Search and Filter - Outside Card */}
                <div style={{ marginBottom: '2rem', zIndex: 12212121, position: 'relative' }}>
                    <div style={{ padding: '1.5rem', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label">البحث بالاسم</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="ابحث عن منتج..."
                                        value={searchName}
                                        onChange={(e) => setSearchName(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <button 
                                        className="btn text-white" 
                                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}
                                        onClick={handleSearch}>
                                        <FaSearch />
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">الفئة</label>
                                <div className="dropdown">
                                    <button 
                                        className="form-select text-start" 
                                        type="button" 
                                        data-bs-toggle="dropdown"
                                        style={{ textAlign: 'right' }}
                                    >
                                        {selectedCategory ? 
                                            categories.find(c => c.id == selectedCategory)?.name?.ar || 
                                            categories.find(c => c.id == selectedCategory)?.name || 'جميع الفئات' 
                                            : 'جميع الفئات'
                                        }
                                    </button>
                                <ul className="dropdown-menu w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {/* Pagination Controls - At the top */}
                                    <li>
                                        <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ backgroundColor: '#e9ecef', borderBottom: '1px solid #dee2e6' }}>
                                            <button 
                                                className="btn btn-sm btn-outline-primary"
                                                disabled={!categoriesPagination.current_page || categoriesPagination.current_page === 1}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const newPage = (categoriesPagination.current_page || 1) - 1;
                                                    setCategoriesPage(newPage);
                                                    fetchCategories(newPage);
                                                }}
                                            >
                                                ‹
                                            </button>
                                            <span className="small fw-bold">
                                                صفحة {categoriesPagination.current_page || 1} من {categoriesPagination.last_page || 1}
                                            </span>
                                            <button 
                                                className="btn btn-sm btn-outline-primary"
                                                disabled={!categoriesPagination.current_page || categoriesPagination.current_page === categoriesPagination.last_page}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const newPage = (categoriesPagination.current_page || 1) + 1;
                                                    setCategoriesPage(newPage);
                                                    fetchCategories(newPage);
                                                }}
                                            >
                                                ›
                                            </button>
                                        </div>
                                    </li>
                                    
                                    <li>
                                        <button 
                                            className="dropdown-item" 
                                            onClick={() => {
                                                setSelectedCategory('');
                                                setCurrentPage(1);
                                            }}
                                        >
                                            جميع الفئات
                                        </button>
                                    </li>
                                    {categories.map((category) => (
                                        <li key={category.id}>
                                            <button 
                                                className="dropdown-item" 
                                                onClick={() => {
                                                    setSelectedCategory(category.id);
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                {category.name?.ar || category.name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">الفئة الفرعية</label>
                                <div className="dropdown">
                                    <button 
                                        className="form-select text-start" 
                                        type="button" 
                                        data-bs-toggle="dropdown"
                                        style={{ textAlign: 'right' }}
                                        disabled={!selectedCategory}
                                    >
                                        {selectedSubCategory ? 
                                            subCategories.find(c => c.id == selectedSubCategory)?.name?.ar || 
                                            subCategories.find(c => c.id == selectedSubCategory)?.name || 'جميع الفئات الفرعية' 
                                            : 'جميع الفئات الفرعية'
                                        }
                                    </button>
                                <ul className="dropdown-menu w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {/* Pagination Controls - At the top */}
                                    <li>
                                        <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ backgroundColor: '#e9ecef', borderBottom: '1px solid #dee2e6' }}>
                                            <button 
                                                className="btn btn-sm btn-outline-primary"
                                                disabled={!subCategoriesPagination.current_page || subCategoriesPagination.current_page === 1}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const newPage = (subCategoriesPagination.current_page || 1) - 1;
                                                    setSubCategoriesPage(newPage);
                                                    fetchSubCategories(selectedCategory, newPage);
                                                }}
                                            >
                                                ‹
                                            </button>
                                            <span className="small fw-bold">
                                                صفحة {subCategoriesPagination.current_page || 1} من {subCategoriesPagination.last_page || 1}
                                            </span>
                                            <button 
                                                className="btn btn-sm btn-outline-primary"
                                                disabled={!subCategoriesPagination.current_page || subCategoriesPagination.current_page === subCategoriesPagination.last_page}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const newPage = (subCategoriesPagination.current_page || 1) + 1;
                                                    setSubCategoriesPage(newPage);
                                                    fetchSubCategories(selectedCategory, newPage);
                                                }}
                                            >
                                                ›
                                            </button>
                                        </div>
                                    </li>
                                    
                                    <li>
                                        <button 
                                            className="dropdown-item" 
                                            onClick={() => {
                                                setSelectedSubCategory('');
                                                setCurrentPage(1);
                                            }}
                                        >
                                            جميع الفئات الفرعية
                                        </button>
                                    </li>
                                    {subCategories.map((subCategory) => (
                                        <li key={subCategory.id}>
                                            <button 
                                                className="dropdown-item" 
                                                onClick={() => {
                                                    setSelectedSubCategory(subCategory.id);
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                {subCategory.name?.ar || subCategory.name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">المتجر</label>
                                <div className="dropdown">
                                    <button 
                                        className="form-select text-start" 
                                        type="button" 
                                        data-bs-toggle="dropdown"
                                        style={{ textAlign: 'right' }}
                                    >
                                        {selectedStore ? 
                                            stores.find(s => s.id == selectedStore)?.market_name || 'جميع المتاجر' 
                                            : 'جميع المتاجر'
                                        }
                                    </button>
                                    <ul className="dropdown-menu w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {/* Pagination Controls - At the top */}
                                        <li>
                                            <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ backgroundColor: '#e9ecef', borderBottom: '1px solid #dee2e6' }}>
                                                <button 
                                                    className="btn btn-sm btn-outline-primary"
                                                    disabled={!storesPagination.current_page || storesPagination.current_page === 1}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const newPage = (storesPagination.current_page || 1) - 1;
                                                        setStoresPage(newPage);
                                                        fetchStores(newPage);
                                                    }}
                                                >
                                                    ‹
                                                </button>
                                                <span className="small fw-bold">
                                                    صفحة {storesPagination.current_page || 1} من {storesPagination.last_page || 1}
                                                </span>
                                                <button 
                                                    className="btn btn-sm btn-outline-primary"
                                                    disabled={!storesPagination.current_page || storesPagination.current_page === storesPagination.last_page}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const newPage = (storesPagination.current_page || 1) + 1;
                                                        setStoresPage(newPage);
                                                        fetchStores(newPage);
                                                    }}
                                                >
                                                    ›
                                                </button>
                                            </div>
                                        </li>
                                        
                                        <li>
                                            <button 
                                                className="dropdown-item" 
                                                onClick={() => {
                                                    setSelectedStore('');
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                جميع المتاجر
                                            </button>
                                        </li>
                                        {stores.map((store) => (
                                            <li key={store.id}>
                                                <button 
                                                    className="dropdown-item" 
                                                    onClick={() => {
                                                        setSelectedStore(store.id);
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    {store.market_name}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <div className="col-md-12 d-flex justify-content-end">
                                <button 
                                    className="btn btn-outline-secondary"
                                    onClick={handleClearFilters}>
                                    مسح الفلاتر
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Products Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل المنتجات...</p>
                            </div>
                        ) : !Array.isArray(products) || products.length === 0 ? (
                            <div className="text-center py-5">
                                <FaBox className="text-muted" style={{ fontSize: '3rem' }} />
                                <p className="mt-3 text-muted">لا توجد منتجات</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-modern">
                                        <thead>
                                            <tr>
                                                <th className="text-center">#</th>
                                                <th className="text-center">الصورة</th>
                                                <th className="text-center">اسم المنتج</th>
                                                <th className="text-center">السعر</th>
                                                <th className="text-center">الخصم</th>
                                                <th className="text-center">المتجر</th>
                                                <th className="text-center">الفرع</th>
                                                <th className="text-center">الفئة</th>
                                                <th className="text-center">الحالة</th>
                                                <th className="text-center">تاريخ الإضافة</th>
                                                <th className="text-center">الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.map((product, index) => (
                                                <tr key={product.id} className="table-row-modern">
                                                    <td className="text-center">
                                                        <div className="order-number-badge">
                                                            #{(currentPage - 1) * 10 + index + 1}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="product-image-container">
                                                            {product.first_image_url ? (
                                                                <img
                                                                    src={resolveProductImageUrl(product.first_image_url)}
                                                                    alt="منتج"
                                                                    className="product-image"
                                                                    referrerPolicy={
                                                                        isGoogleDrivePageUrl(product.first_image_url)
                                                                            ? 'no-referrer'
                                                                            : undefined
                                                                    }
                                                                    loading="lazy"
                                                                    onError={(e) => {
                                                                        const id = extractGoogleDriveFileId(
                                                                            product.first_image_url
                                                                        );
                                                                        if (!id) {
                                                                            e.currentTarget.onerror = null;
                                                                            return;
                                                                        }
                                                                        const el = e.currentTarget;
                                                                        const step = Number(
                                                                            el.dataset.driveFallback || 0
                                                                        );
                                                                        if (step === 0) {
                                                                            el.dataset.driveFallback = '1';
                                                                            el.src = `https://drive.google.com/thumbnail?id=${id}&sz=w1000`;
                                                                        } else if (step === 1) {
                                                                            el.dataset.driveFallback = '2';
                                                                            el.src = `https://lh3.googleusercontent.com/d/${id}`;
                                                                        } else {
                                                                            el.onerror = null;
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="no-image-placeholder">
                                                                    <FaImage />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="product-name">
                                                            {product.name || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="amount-display">
                                                            {product.default_price ? `${product.default_price} ج.م` : '-'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="discount-badge">
                                                            {product.discount ? `${product.discount}%` : '-'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="store-name">
                                                            {product.store_name || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="branch-name">
                                                            {product.branch_name || '-'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="category-name">
                                                            {product.category_name || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        {getStatusBadge(product.status)}
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="date-display">
                                                            {product.created_at ? new Date(product.created_at).toLocaleDateString('ar-EG') : 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="action-buttons">
                                                            <motion.button
                                                                whileHover={{ scale: 1.1, y: -2 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-view"
                                                                onClick={() => handleViewDetails(product.id)}
                                                                title="عرض التفاصيل">
                                                                <FaEye />
                                                            </motion.button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination && renderPagination()}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminProducts;

// CSS للتصميم الحديث
const style = document.createElement('style');
style.textContent = `
  /* تصميم الجدول الحديث */
  .table-modern {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: none;
  }
  
  .table-modern thead {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }
  
  .table-modern thead th {
    border: none;
    padding: 1rem;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .table-modern tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.3s ease;
  }
  
  .table-modern tbody tr:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .table-modern tbody td {
    border: none;
    padding: 1rem;
    vertical-align: middle;
  }
  
  /* تصميم رقم المنتج */
  .order-number-badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.875rem;
    display: inline-block;
    min-width: 60px;
    text-align: center;
  }
  
  /* تصميم صورة المنتج */
  .product-image-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .product-image {
    width: 50px;
    height: 50px;
    border-radius: 8px;
    object-fit: cover;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .no-image-placeholder {
    width: 50px;
    height: 50px;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 1.2rem;
  }
  
  /* أيقونات افتراضية للبيانات الفارغة */
  .default-placeholder {
    width: 50px;
    height: 50px;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 1.2rem;
    transition: all 0.3s ease;
  }
  
  .default-placeholder:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #64748b;
  }
  
  /* تصميم اسم المنتج */
  .product-name {
    font-weight: 500;
    color: #374151;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  /* تصميم السعر */
  .amount-display {
    background: #f0fdf4;
    color: #166534;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    border: 1px solid #bbf7d0;
  }
  
  /* تصميم الخصم */
  .discount-badge {
    background: #fef3c7;
    color: #92400e;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    border: 1px solid #fde68a;
  }
  
  /* تصميم اسم المتجر */
  .store-name {
    font-weight: 500;
    color: #374151;
  }
  
  /* تصميم اسم الفرع */
  .branch-name {
    color: #64748b;
    font-size: 0.875rem;
  }
  
  /* تصميم اسم الفئة */
  .category-name {
    color: #64748b;
    font-size: 0.875rem;
  }
  
  /* تصميم التاريخ */
  .date-display {
    background: #f8fafc;
    color: #64748b;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #e2e8f0;
  }
  
  /* تصميم أزرار الإجراءات */
  .action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
  }
  
  .btn-view {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white !important;
    padding: 0.25rem;
    border-radius: 8px;
    font-size: 1.2rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
    width: 36px;
    height: 36px;
  }
  
  .btn-edit {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: none;
    color: white !important;
    padding: 0.25rem;
    border-radius: 8px;
    font-size: 1.2rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
    width: 36px;
    height: 36px;
  }
  
  .btn-delete {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border: none;
    color: white !important;
    padding: 0.25rem;
    border-radius: 8px;
    font-size: 1.2rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
    width: 36px;
    height: 36px;
  }
  
  .btn-view * {
    color: white !important;
  }
  
  .btn-view:hover, .btn-edit:hover, .btn-delete:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  .btn-view:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  .btn-edit:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  }
  
  .btn-delete:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }
  
  /* تحسين البادج */
  .badge {
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  /* إصلاح الباجينيشن */
  .pagination .page-item.active .page-link {
    background-color: #667eea !important;
    border-color: #667eea !important;
    color: white !important;
  }
  .pagination .page-item .page-link {
    color: #667eea;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin: 0 2px;
    padding: 0.5rem 0.75rem;
  }
  .pagination .page-item .page-link:hover {
    color: #5a67d8;
    background-color: #f8fafc;
    border-color: #cbd5e1;
  }

  /* تحسين الدروب دون */
  .dropdown-menu {
    z-index: 1050 !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    border: 1px solid #e2e8f0;
  }

  .dropdown {
    position: relative;
    z-index: 1040;
  }

  /* إصلاح الـ card للسماح بـ dropdown */
  .card {
    overflow: visible !important;
    position: relative;
    z-index: 1;
  }

  .dashboard-container {
    overflow: visible !important;
  }

  .card-body {
    overflow: visible !important;
    position: relative;
    z-index: 1;
  }

  .row {
    overflow: visible !important;
    position: relative;
    z-index: 1;
  }

  .col-md-3 {
    overflow: visible !important;
    position: relative;
    z-index: 1;
  }
`;
document.head.appendChild(style);